import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { personalInfo, projects, ceoEndorsement } from "../../data/portfolioData";
import { FaApple, FaGooglePlay, FaGithub, FaLinkedinIn, FaTwitter, FaEnvelope, FaArrowRight, FaDownload, FaQuoteLeft, FaGlobe } from "react-icons/fa";
import { FiCopy, FiCheck } from "react-icons/fi";
import { PhoneMockup } from "../DeviceMockup";
import ProjectModal from "../Projects/ProjectModal";
import pdfCV from "../../Assets/MRayyanCV.pdf";

function Home() {
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [spotlightProject, setSpotlightProject] = useState(projects[0]); // Default to Tuba
  const [selectedModalProject, setSelectedModalProject] = useState(null);

  const featuredApps = projects.filter((p) => p.category === "mobile").slice(0, 4);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(personalInfo.email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  return (
    <div className="home-wrapper">
      {/* Hero Section */}
      <section className="hero-section" id="hero">
        <div className="hero-mesh-background"></div>
        <Container>
          <Row className="align-items-center hero-row g-5">
            <Col lg={7} md={12} className="hero-text-col">
              {/* Live Status Pill */}
              <motion.div
                className="status-pill-badge"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <span className="live-radar-dot"></span>
                <span>{personalInfo.status}</span>
              </motion.div>

              {/* Main Headline */}
              <motion.h1
                className="hero-headline text-start"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                I'm <span className="gradient-text">{personalInfo.name}</span>
                <br />
                <span className="hero-subheadline">
                  Full Stack & <span className="highlight-text">React Native</span> Developer.
                </span>
              </motion.h1>

              {/* Bio Description */}
              <motion.p
                className="hero-description text-start"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
              >
                {personalInfo.bio}
              </motion.p>

              {/* Key Store Proof Bar */}
              <motion.div
                className="store-proof-row"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <span className="proof-label">Published Applications Live On:</span>
                <div className="proof-badges">
                  <span className="proof-badge apple"><FaApple /> iOS App Store</span>
                  <span className="proof-badge google"><FaGooglePlay /> Google Play Store</span>
                </div>
              </motion.div>

              {/* CTAs */}
              <motion.div
                className="hero-cta-group"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25 }}
              >
                <Link to="/project" className="btn-modern primary">
                  <span>Explore Apps & Works</span>
                  <FaArrowRight />
                </Link>

                <a
                  href={pdfCV}
                  download="Rayyan_Waseem_CV.pdf"
                  className="btn-modern secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaDownload />
                  <span>Download CV</span>
                </a>

                <button
                  className={`btn-modern outline email-copy-btn ${copiedEmail ? "copied" : ""}`}
                  onClick={handleCopyEmail}
                  title="Copy email to clipboard"
                >
                  {copiedEmail ? <FiCheck className="text-emerald" /> : <FiCopy />}
                  <span>{copiedEmail ? "Copied Email!" : "Copy Email"}</span>
                </button>
              </motion.div>

              {/* Quick Socials */}
              <motion.div
                className="hero-socials-row"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <a href={personalInfo.socials.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                  <FaGithub />
                </a>
                <a href={personalInfo.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <FaLinkedinIn />
                </a>
                <a href={personalInfo.socials.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <FaTwitter />
                </a>
                <a href={`mailto:${personalInfo.email}`} aria-label="Email">
                  <FaEnvelope />
                </a>
              </motion.div>
            </Col>

            {/* Interactive Spotlight Hero Preview */}
            <Col lg={5} md={12} className="hero-mockup-col">
              <motion.div
                className="hero-interactive-spotlight"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                <div className="spotlight-card-glass">
                  <div className="spotlight-selector-tabs">
                    {featuredApps.map((app) => (
                      <button
                        key={app.id}
                        className={`spotlight-tab ${spotlightProject.id === app.id ? "active" : ""}`}
                        onClick={() => setSpotlightProject(app)}
                      >
                        {app.title.split(" ")[0]}
                      </button>
                    ))}
                  </div>

                  <div className="spotlight-preview-viewport">
                    <PhoneMockup project={spotlightProject} />
                  </div>

                  <div className="spotlight-footer">
                    <div className="spotlight-meta text-start">
                      <h5>{spotlightProject.title}</h5>
                      <p>{spotlightProject.subtitle}</p>
                    </div>
                    <button
                      className="spotlight-inspect-btn"
                      onClick={() => setSelectedModalProject(spotlightProject)}
                    >
                      Inspect <FaArrowRight />
                    </button>
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Metrics Bar */}
      <section className="metrics-banner-section">
        <Container>
          <div className="metrics-grid">
            {personalInfo.metrics.map((metric, idx) => (
              <motion.div
                key={idx}
                className="metric-card"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.35, delay: idx * 0.08 }}
              >
                <h3 className="metric-number">{metric.value}</h3>
                <p className="metric-title">{metric.label}</p>
                <span className="metric-sub">{metric.highlight}</span>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Featured Apps Showcase Preview */}
      <section className="featured-apps-section">
        <Container>
          <div className="section-head text-center">
            <div className="badge-pill mb-3">
              <span>📱 Featured App Showcase</span>
            </div>
            <h2 className="section-title">
              Featured <span className="gradient-text">Mobile Applications</span>
            </h2>
            <p className="section-lead">
              Client-facing production applications published to the App Store and Google Play with multi-threaded performance and native capabilities.
            </p>
          </div>

          <Row className="featured-apps-grid g-4 align-items-stretch">
            {featuredApps.map((app, index) => (
              <Col lg={6} md={12} key={app.id} className="d-flex">
                <motion.div
                  className="featured-app-card flex-fill"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{ duration: 0.35, delay: index * 0.08 }}
                >
                  <div
                    className="featured-preview-box"
                    style={{ background: app.gradient }}
                    onClick={() => setSelectedModalProject(app)}
                  >
                    <div className="preview-scale-wrapper">
                      <PhoneMockup project={app} />
                    </div>
                  </div>

                  <div className="featured-card-body text-start">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span
                        className="app-badge-pill"
                        style={{
                          color: app.color,
                          backgroundColor: `${app.color}15`,
                          borderColor: `${app.color}45`,
                        }}
                      >
                        {app.badge}
                      </span>
                      <span className="platform-tag">{app.platform}</span>
                    </div>

                    <h3>{app.title}</h3>
                    <p className="app-sub">{app.subtitle}</p>
                    <p className="app-desc">{app.shortDesc}</p>

                    <div className="app-tags mb-3">
                      {app.tags.slice(0, 4).map((tag, i) => (
                        <span key={i} className="tech-badge">{tag}</span>
                      ))}
                    </div>

                    <div className="featured-card-actions">
                      <div className="store-btns">
                        {app.links.appStore && (
                          <a href={app.links.appStore} target="_blank" rel="noopener noreferrer" className="mini-store-link ios">
                            <FaApple /> App Store
                          </a>
                        )}
                        {app.links.playStore && (
                          <a href={app.links.playStore} target="_blank" rel="noopener noreferrer" className="mini-store-link android">
                            <FaGooglePlay /> Play Store
                          </a>
                        )}
                        {app.links.web && (
                          <a href={app.links.web} target="_blank" rel="noopener noreferrer" className="mini-store-link web">
                            <FaGlobe /> Website
                          </a>
                        )}
                      </div>
                      <button
                        className="inspect-btn"
                        onClick={() => setSelectedModalProject(app)}
                      >
                        Deep Dive <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-5">
            <Link to="/project" className="btn-modern primary">
              <span>View All 8+ Apps, Systems & Tools</span>
              <FaArrowRight />
            </Link>
          </div>
        </Container>
      </section>

      {/* CEO Endorsement Spotlight */}
      <section className="ceo-recommendation-section">
        <Container>
          <motion.div
            className="endorsement-card-large"
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="quote-badge">
              <FaQuoteLeft />
            </div>
            <p className="endorsement-quote-text">
              "{ceoEndorsement.text}"
            </p>
            <div className="endorsement-author-row">
              <div className="d-flex align-items-center">
                <div className="author-avatar-badge">AM</div>
                <div className="author-meta text-start">
                  <h4>{ceoEndorsement.name}</h4>
                  <p>{ceoEndorsement.role} • {ceoEndorsement.location}</p>
                </div>
              </div>
              <div className="author-action-btns">
                <a
                  href={`mailto:${ceoEndorsement.email}`}
                  className="btn-modern outline email-author-btn"
                >
                  <FaEnvelope /> <span>{ceoEndorsement.email}</span>
                </a>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Quick Recruiter Contact Bar */}
      <section className="quick-contact-section">
        <Container>
          <div className="quick-contact-box">
            <div className="contact-box-left text-start">
              <h2>Let's build your next high-performance application.</h2>
              <p>Available for React Native, Full-Stack, and Mobile Engineering opportunities.</p>
            </div>
            <div className="contact-box-right">
              <a href={`mailto:${personalInfo.email}`} className="btn-modern primary">
                <FaEnvelope /> <span>Send an Email</span>
              </a>
              <a
                href={pdfCV}
                download="Rayyan_Waseem_CV.pdf"
                className="btn-modern secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaDownload /> <span>Download Resume</span>
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* Modal for Deep Dive */}
      <ProjectModal
        project={selectedModalProject}
        isOpen={Boolean(selectedModalProject)}
        onClose={() => setSelectedModalProject(null)}
      />
    </div>
  );
}

export default Home;
